import React, { useState, useRef, useEffect } from "react";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const GREETING = "Hello — I'm Aria, your Adastra concierge. Tell me what you're looking for and I'll recommend pieces from our collection.";

export const ConciergeChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "concierge", content: GREETING }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionId = useRef(`concierge-${Math.random().toString(36).slice(2)}-${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "buyer", content: text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/ai/concierge", { session_id: sessionId.current, message: text });
      setMessages((m) => [...m, { role: "concierge", content: data.reply || "…" }]);
    } catch {
      setMessages((m) => [...m, { role: "concierge", content: "I'm having trouble right now. Please try again in a moment." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open concierge assistant"
          data-testid="concierge-fab"
          className="fixed bottom-5 left-5 z-50 flex h-12 items-center gap-2 rounded-full bg-foreground px-5 text-background shadow-lg shadow-black/20 transition-transform duration-200 hover:scale-105 active:scale-95"
        >
          <Sparkles className="h-4 w-4" strokeWidth={1.75} />
          <span className="text-sm font-medium">Ask Aria</span>
        </button>
      )}

      {open && (
        <div
          data-testid="concierge-panel"
          className="fixed bottom-5 left-5 z-50 flex h-[70vh] max-h-[560px] w-[calc(100vw-2.5rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-black/10 bg-white shadow-2xl shadow-black/25"
        >
          <div className="flex items-center justify-between border-b border-black/5 bg-foreground px-4 py-3 text-background">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" strokeWidth={1.75} />
              <div>
                <p className="text-sm font-medium leading-tight">Aria</p>
                <p className="text-[11px] opacity-70 leading-tight">Adastra Concierge</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" data-testid="concierge-close" className="rounded-full p-1 hover:bg-white/10">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`concierge-message-${i}`}
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  m.role === "buyer"
                    ? "ml-auto bg-foreground text-background rounded-br-sm"
                    : "mr-auto bg-secondary text-foreground rounded-bl-sm"
                }`}
              >
                {m.content}
              </div>
            ))}
            {sending && (
              <div className="mr-auto flex items-center gap-2 rounded-2xl bg-secondary px-3.5 py-2.5 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Aria is thinking…
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 border-t border-black/5 p-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder="Ask about our collection…"
              className="h-11 rounded-full"
              data-testid="concierge-input"
            />
            <Button
              onClick={send}
              disabled={sending || !input.trim()}
              size="icon"
              className="h-11 w-11 shrink-0 rounded-full bg-foreground text-background hover:bg-foreground/90"
              data-testid="concierge-send"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default ConciergeChat;
